import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface SupplierBrandFormData {
  brandId: string;
  isAuthorized: boolean;
  isPrimary: boolean;
  notes?: string;
}

interface SupplierBrandModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplierId: string;
  supplierName: string;
  existingBrandIds?: string[];
}

export function SupplierBrandModal({
  open,
  onOpenChange,
  supplierId,
  supplierName,
  existingBrandIds = [],
}: SupplierBrandModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    register,
  } = useForm<SupplierBrandFormData>({
    defaultValues: {
      brandId: '',
      isAuthorized: true,
      isPrimary: false,
      notes: '',
    },
  });

  const utils = trpc.useUtils();

  const { data: brandsData } = trpc.tradingBrand.listForSelect.useQuery();

  const availableBrands = brandsData?.filter(
    (b) => !existingBrandIds.includes(b.id)
  ) || [];

  const brandOptions = availableBrands.map((b) => ({
    value: b.id,
    label: b.name,
  }));

  const createMutation = trpc.tradingBrandSupplier.create.useMutation({
    onSuccess: () => {
      utils.tradingBrandSupplier.getBrandsBySupplier.invalidate(supplierId);
      showSuccessToast(tCommon('created'));
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const onSubmit = (data: SupplierBrandFormData) => {
    createMutation.mutate({
      brandId: data.brandId,
      supplierId,
      isAuthorized: data.isAuthorized,
      isPrimary: data.isPrimary,
      notes: data.notes || undefined,
    });
  };

  const selectedBrandId = watch('brandId');
  const isSubmitting = createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('purchasing.suppliers.brands.addBrand')}</DialogTitle>
          <DialogDescription>
            {t('purchasing.suppliers.brands.subtitle')} - {supplierName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>{t('brands.title')}</Label>
            <Combobox
              options={brandOptions}
              value={selectedBrandId}
              onValueChange={(value) => setValue('brandId', value)}
              placeholder={t('purchasing.suppliers.brands.selectBrand')}
              searchPlaceholder={tCommon('search')}
              emptyText={t('purchasing.suppliers.brands.noBrandsAvailable')}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isAuthorized"
              checked={watch('isAuthorized')}
              onCheckedChange={(checked) => setValue('isAuthorized', !!checked)}
            />
            <Label htmlFor="isAuthorized" className="cursor-pointer">
              {t('purchasing.suppliers.brands.isAuthorized')}
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isPrimary"
              checked={watch('isPrimary')}
              onCheckedChange={(checked) => setValue('isPrimary', !!checked)}
            />
            <Label htmlFor="isPrimary" className="cursor-pointer">
              {t('purchasing.suppliers.brands.isPrimary')}
            </Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('purchasing.suppliers.brands.notes')}</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !selectedBrandId}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
